// Cloudflare Turnstile Challenge Modal for Desktop Trading Web

export interface TurnstileChallengeProps {
  siteKey: string;
  onSolve: (token: string) => void;
  onCancel: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          cData?: string;
          callback: (token: string) => void;
          'error-callback'?: (error: any) => void;
          'expired-callback'?: () => void;
          theme?: 'dark' | 'light' | 'auto';
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onTurnstileLoaded?: () => void;
  }
}

export class TurnstileChallengeModal {
  private modalEl: HTMLElement | null = null;
  private widgetId: string | null = null;
  private props: TurnstileChallengeProps;

  constructor(props: TurnstileChallengeProps) {
    this.props = props;
  }

  public show(): void {
    this.close();

    const backdrop = document.createElement('div');
    backdrop.id = 'turnstile-challenge-modal-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(10, 15, 29, 0.85);
      backdrop-filter: blur(8px);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      background: #131b2e;
      border: 1px solid #1e293b;
      border-radius: 16px;
      padding: 28px;
      max-width: 420px;
      width: 100%;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
      text-align: center;
      color: #f8fafc;
    `;

    const isLocalDev = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      !(window as any).__STARX_PRODUCTION__ && this.props.siteKey === '1x00000000000000000000AA';

    card.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
        <div style="width: 8px; height: 8px; border-radius: 50%; background: #3b82f6;"></div>
        <h3 style="margin: 0; font-size: 15px; font-weight: 600; color: #f8fafc;">Security Verification</h3>
      </div>

      <p style="font-size: 13px; line-height: 1.5; color: #94a3b8; margin: 0 0 16px 0;">
        Please complete the security check to confirm your trade execution.
      </p>

      <div id="turnstile-widget-container" style="min-height: 70px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px;">
        <div style="font-size: 12px; color: #64748b;">Loading verification challenge...</div>
      </div>

      <div style="display: flex; gap: 10px; justify-content: center;">
        ${isLocalDev ? `
        <button id="turnstile-simulate-btn" style="
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        ">⚡ Verify Instantly (Dev/Test)</button>` : ''}
        <button id="turnstile-cancel-btn" style="
          padding: 8px 16px;
          background: #1e293b;
          color: #94a3b8;
          border: 1px solid #334155;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        ">Cancel</button>
      </div>
    `;

    backdrop.appendChild(card);
    document.body.appendChild(backdrop);
    this.modalEl = backdrop;

    const cancelBtn = card.querySelector('#turnstile-cancel-btn') as HTMLButtonElement;
    if (cancelBtn) {
      cancelBtn.onclick = () => {
        this.close();
        this.props.onCancel();
      };
    }

    const simBtn = card.querySelector('#turnstile-simulate-btn') as HTMLButtonElement;
    if (simBtn) {
      simBtn.onclick = () => {
        const dummyToken = `turnstile_simulated_token_${Date.now()}`;
        this.close();
        this.props.onSolve(dummyToken);
      };
    }

    this.mountTurnstile(card);
  }

  private mountTurnstile(card: HTMLElement): void {
    const container = card.querySelector('#turnstile-widget-container') as HTMLElement;
    if (!container) return;

    const siteKey = this.props.siteKey || '1x00000000000000000000AA';

    const renderWidget = () => {
      if (window.turnstile && container) {
        container.innerHTML = '';
        try {
          this.widgetId = window.turnstile.render(container, {
            sitekey: siteKey,
            action: 'TRADE',
            theme: 'dark',
            callback: (token: string) => {
              this.close();
              this.props.onSolve(token);
            },
            'error-callback': (err: any) => {
              console.warn('[Turnstile] Challenge error:', err);
            },
          });
        } catch (e) {
          console.warn('[Turnstile] Render failed:', e);
        }
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Dynamically inject Cloudflare Turnstile script if not present
    const existingScript = document.querySelector('script[src*="turnstile/v0/api.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(renderWidget, 100);
      };
      script.onerror = () => {
        if (container) {
          container.innerHTML = '<div style="font-size: 12px; color: #f59e0b;">Challenge service unreachable. Cancel and retry when connectivity returns.</div>';
        }
      };
      document.head.appendChild(script);
    } else {
      setTimeout(renderWidget, 200);
    }
  }

  public close(): void {
    if (this.widgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId);
      } catch {}
      this.widgetId = null;
    }
    if (this.modalEl && this.modalEl.parentNode) {
      this.modalEl.parentNode.removeChild(this.modalEl);
      this.modalEl = null;
    }
  }
}
